import React, { forwardRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { apiCreateBroker, apiGetBroker } from '@store';
import { useDispatch } from 'react-redux';
import { showToast } from '@utils';
import { useCountries } from '../../../hooks/useCountry';

const allowedCountries = [
    { name: 'United Kingdom', code: '+44' },
    { name: 'Switzerland', code: '+41' },
    { name: 'Germany', code: '+49' },
    { name: 'Poland', code: '+48' },
    { name: 'France', code: '+33' },
    { name: 'Belgium', code: '+32' },
    { name: 'Hungary', code: '+36' }
];

const CreateBroker = forwardRef(({ handleClose }, ref) => {
    const dispatch = useDispatch();
    const [state, setState] = useState({
        name: '',
        country: '',
        location: '',
        phone: '',
        country_code: '',
        bank_details: ''
    });
    const { countries, loading, error } = useCountries();

    const handleChange = (e) => {
        const key = e.target.name || e.target.id;
        if (key === 'country') {
            const allowed = allowedCountries.find((c) => c.name === e.target.value);
            setState({
                ...state,
                country: e.target.value,
                country_code: allowed?.code || ''
            });
            return;
        }
        setState({
            ...state,
            [key]: key === 'phone' ? e.target.value.replace(/\D/g, '') : e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { country_code, ...rest } = state;
        const data = {
            ...rest,
            phone: country_code + state.phone
        };
        const response = await dispatch(apiCreateBroker(data));
        if (response.meta.requestStatus === 'fulfilled') {
            dispatch(apiGetBroker());
            handleClose();
            showToast('success', 'Broker created successfully');
        } else if (response.meta.requestStatus === 'rejected') {
            showToast('error', response.error?.message || response.payload || 'Failed to create broker');
        } else {
            showToast('error', 'An error occurred');
        }
    };

    return (
        <Box
            className="modal-container"
            component="form"
            onSubmit={handleSubmit}
        >
            <Box className="flex justify-between modal-header">
                <h2 className="modal-title">Create broker</h2>
                <CloseIcon className="cursor-pointer" onClick={handleClose} />
            </Box>
            <div className="modal-body">
                <FormControl fullWidth={true} variant="outlined" sx={{ mb: 2 }}>
                    <InputLabel id="nameLabel">Name *</InputLabel>
                    <OutlinedInput
                        id="name"
                        name="name"
                        label="Name *"
                        value={state.name}
                        onChange={handleChange}
                        required
                    />
                </FormControl>
                <FormControl fullWidth={true} variant="outlined" sx={{ mb: 2 }} error={!!error}>
                    <InputLabel id="countryLabel">Country *</InputLabel>
                    <Select
                        labelId="countryLabel"
                        id="country"
                        name="country"
                        value={state.country || ''}
                        onChange={handleChange}
                        label="Country *"
                        required
                        disabled={loading}
                        endAdornment={loading ? <CircularProgress size={20} sx={{ mr: 3 }} /> : null}
                    >
                        {error ? (
                            <MenuItem disabled value="">
                                <em>Error loading countries</em>
                            </MenuItem>
                        ) : (
                            countries
                                .filter((countryName) => allowedCountries.some((c) => c.name === countryName))
                                .map((countryName) => (
                                    <MenuItem key={countryName} value={countryName}>
                                        {countryName}
                                    </MenuItem>
                                ))
                        )}
                    </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: '10px', mb: 2 }}>
                    <FormControl sx={{ width: '90px' }}>
                        <InputLabel id="countryCodeLabel">Code</InputLabel>
                        <Select
                            labelId="countryCodeLabel"
                            id="country_code"
                            name="country_code"
                            value={state.country_code}
                            onChange={handleChange}
                            label="Code"
                            disabled
                        >
                            {allowedCountries.map((country) => (
                                <MenuItem key={country.code} value={country.code}>
                                    {country.code}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1, minWidth: 0 }} variant="outlined">
                        <InputLabel id="phoneLabel">Phone</InputLabel>
                        <OutlinedInput
                            id="phone"
                            name="phone"
                            label="Phone *"
                            type="tel"
                            value={state.phone}
                            onChange={handleChange}
                        />
                    </FormControl>
                </Box>
                <FormControl fullWidth={true} variant="outlined" sx={{ mb: 2 }}>
                    <InputLabel id="locationLabel">Location *</InputLabel>
                    <OutlinedInput
                        id="location"
                        name="location"
                        label="Location *"
                        value={state.location}
                        onChange={handleChange}
                        required
                    />
                </FormControl>
            </div>
            <div className="modal-footer">
                <Button
                    className="modal-button-secondary"
                    onClick={handleClose}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    className="modal-button-primary"
                    disabled={!state.name || !state.country || !state.location}
                >
                    Submit
                </Button>
            </div>
        </Box>
    );
});

export default CreateBroker;

